import { Box, useMantineTheme } from '@mantine/core';
import { keyframes } from '../utils/keyframes';;
import { forwardRef } from 'react';
import { useReducedMotion } from '../hooks';

const indeterminateKeyframes = keyframes({
  '0%': { left: '-40%', width: '40%' },
  '50%': { left: '20%', width: '80%' },
  '100%': { left: '100%', width: '40%' },
});

export interface ProgressBarProps {
  value?: number; // 0-100, undefined for indeterminate
  height?: number;
  color?: string;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
  radius?: number | string;
  animated?: boolean;
  striped?: boolean;
}

const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  (
    {
      value,
      height = 4,
      color,
      gradientVariant = 'primary',
      radius = 'xl',
      animated = true,
      striped = false,
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const reducedMotion = useReducedMotion();
    const gradient = theme.other?.gradients?.[gradientVariant];
    const barColor = color || `linear-gradient(90deg, ${gradient?.from}, ${gradient?.to})`;

    const isIndeterminate = value === undefined;

    const stripedKeyframes = keyframes({
      '0%': { backgroundPosition: '0 0' },
      '100%': { backgroundPosition: `${height * 2}px 0` },
    });

    const stripedBackground = striped
      ? `repeating-linear-gradient(
          45deg,
          transparent,
          transparent ${height / 2}px,
          rgba(255,255,255,0.2) ${height / 2}px,
          rgba(255,255,255,0.2) ${height}px
        )`
      : undefined;

    return (
      <Box
        ref={ref}
        style={{
          height,
          backgroundColor: theme.colors.gray[2],
          borderRadius: theme.radius[radius as keyof typeof theme.radius] || radius,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: isIndeterminate ? '40%' : `${value}%`,
            background: barColor,
            backgroundImage: stripedBackground,
            backgroundSize: striped ? `${height * 2}px ${height * 2}px` : undefined,
            borderRadius: 'inherit',
            transition: isIndeterminate ? 'none' : 'width 0.3s ease',
            animation: reducedMotion
              ? 'none'
              : isIndeterminate
                ? `${indeterminateKeyframes} 1.5s ease-in-out infinite`
                : striped && animated
                  ? `${stripedKeyframes} 1s linear infinite`
                  : 'none',
          }}
        />
      </Box>
    );
  }
);

ProgressBar.displayName = 'ProgressBar';

export default ProgressBar;
